import { AfterViewInit, ChangeDetectorRef, ElementRef, HostBinding, HostListener, Input, Renderer2 } from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import autobind from "autobind-decorator";
import * as nanoidImport from "nanoid";

// Some imports need this to work in all contexts incl ng-packagr/rollup
const nanoid = (<any>nanoidImport).default || nanoidImport;

import { SpsFormComponent } from "./sps-form-component.abstract.component";

export abstract class SpsFormControlComponent<T> extends SpsFormComponent
    implements AfterViewInit, ControlValueAccessor {
    inputElement: ElementRef;
    protected _onTouched: (event: any) => void;
    protected _propagateChange: (newModelValue?: T) => void;
    protected _changeDetector: ChangeDetectorRef;

    @Input() id = nanoid();

    protected _model: T;
    get model(): T {
        return this._model;
    }
    set model(newValue: T) {
        this._model = newValue;
        if (this._propagateChange) {
            this._propagateChange(this._model);
        }
    }

    get _control() {
        return this._ngControl && this._ngControl.control;
    }

    private _isDisabled = false;
    @HostBinding("class.sps-form-control--disabled")
    get isDisabled(): boolean {
        return this._isDisabled;
    }

    @HostBinding("class.sps-form-control--focused") isFocused = false;

    @HostBinding("attr.inputId") get inputId() {
        return `${this.id}__input`;
    }

    @HostListener("focus")
    onFocus() {
        if (this.inputElement) {
            this.inputElement.nativeElement.focus();
        }
    }

    constructor(
        protected _el: ElementRef,
        protected _renderer: Renderer2,
        protected _ngControl?: NgControl
    ) {
        super();
        if (_ngControl) {
            _ngControl.valueAccessor = this;
        }
    }

    ngAfterViewInit() {
        if (this._el) {
            // Transfer certain attributes from component root element to actual <input> element if need be
            if (this.inputElement) {
                for (const attr of ["tabindex", "aria-label"]) {
                    if (this._el.nativeElement.hasAttribute(attr)) {
                        const value = this._el.nativeElement.getAttribute(attr);
                        this._renderer.removeAttribute(this._el.nativeElement, attr);
                        this._renderer.setAttribute(this.inputElement.nativeElement, attr, value);
                    }
                }
            }

            (this.inputElement || this._el).nativeElement.addEventListener(
                "focus",
                this._setFocused
            );
            (this.inputElement || this._el).nativeElement.addEventListener(
                "blur",
                this._setBlurred
            );
        }
    }

    @autobind
    private _setFocused() {
        this.isFocused = true;
    }

    @autobind
    private _setBlurred(blurEvent: any) {
        this.isFocused = false;
        if (this._onTouched) {
            this._onTouched(blurEvent);
        }
    }

    writeValue(value: T): void {
        this._model = value;
        if (this._changeDetector) {
            this._changeDetector.markForCheck();
        }
    }

    registerOnChange(fn: any): void {
        this._propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
    }
}
